// TourPanel — the fixed side panel that renders the active guided-tour step.
//
// Neutral design-system styling (own CSS, brand tokens shared with the shell);
// deliberately NOT the rapid-* classes. Renders nothing when no tour is active.
// On the last step Next becomes "Finish", which records completion and reveals
// the closing narrative with a Done button.
import { useTours } from './TourProvider.jsx';
import { DEFAULT_FALLBACK_BODY } from './tourAnchors.js';
import { wrapGlossaryTerms } from '../../components/primitives/GlossaryTerm.jsx';
import './tourPanel.css';

export function TourPanel() {
  const { activeTourId, tour, currentStep, stepIndex, stepCount, showClosing, anchorMissing, next, back, stop } =
    useTours();

  if (!activeTourId || !tour || !currentStep) return null;

  const isLast = stepIndex >= stepCount - 1;

  return (
    <aside
      className="tour-panel"
      data-testid="tour-panel"
      role="dialog"
      aria-label={`${tour.title} guided tour`}
      aria-live="polite"
    >
      <header className="tour-panel__head">
        <div className="tour-panel__heading">
          <span className="tour-panel__kicker">Guided tour</span>
          <span className="tour-panel__title">{tour.title}</span>
        </div>
        <span className="tour-panel__count" aria-label={`Step ${stepIndex + 1} of ${stepCount}`}>
          {stepIndex + 1} / {stepCount}
        </span>
        <button
          type="button"
          className="tour-panel__close"
          aria-label="Close tour"
          onClick={stop}
        >
          ×
        </button>
      </header>

      {showClosing ? (
        <div className="tour-panel__closing">
          <p className="tour-panel__closing-body">{wrapGlossaryTerms(tour.closingNarrative)}</p>
          <div className="tour-panel__footer">
            <button type="button" className="tour-btn tour-btn--primary" data-testid="tour-finish" onClick={stop}>
              Done
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="tour-panel__body" data-testid={`tour-step-${currentStep.id}`}>
            <h3 className="tour-panel__step-title">{currentStep.title}</h3>
            <p className="tour-panel__step-body">{wrapGlossaryTerms(currentStep.body)}</p>
            {anchorMissing ? (
              <p className="tour-panel__fallback" data-testid="tour-anchor-fallback">
                {wrapGlossaryTerms(currentStep.fallbackBody || DEFAULT_FALLBACK_BODY)}
              </p>
            ) : null}
          </div>
          <div className="tour-panel__footer">
            <button
              type="button"
              className="tour-btn tour-btn--ghost"
              data-testid="tour-back"
              onClick={back}
              disabled={stepIndex === 0}
            >
              Back
            </button>
            <button type="button" className="tour-btn tour-btn--ghost" data-testid="tour-skip" onClick={stop}>
              Skip tour
            </button>
            <button type="button" className="tour-btn tour-btn--primary" data-testid="tour-next" onClick={next}>
              {isLast ? 'Finish' : 'Next'}
            </button>
          </div>
        </>
      )}
    </aside>
  );
}
